import React from 'react'
import { faFileArrowUp } from '@fortawesome/free-solid-svg-icons'
import {
  DcButton,
  DcDiv,
  DcDropDown,
  DcFlexBox,
  DcText,
  PixelFactoryContext,
  PixelIcon,
  colors,
} from 'pixel-base'
import type { AjaxError, AjaxResponse } from 'rxjs/ajax'
import styled from 'styled-components'
import { submitLeadTimes } from 'utils/api-routes-planning'
import type { BulkUploadResponse } from 'utils/api-routes-planning'
import type { LeadTimeRow } from 'utils/api-routes-planning'
import * as XLSX from 'xlsx'
import { IPixelFactory } from '../../../../../decl.d'

const DropZone = styled.label<{ $isDragging: boolean }>`
  width: 100%;
  border: 1px dashed
    ${({ $isDragging }) => ($isDragging ? '#5c4dff' : '#dcdfe6')};
  border-radius: 12px;
  padding: 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  gap: 10px;
  cursor: pointer;
  background-color: ${({ $isDragging }) =>
    $isDragging ? 'rgba(92,77,255,0.05)' : 'transparent'};
  transition: all 0.2s ease-in-out;
`

const ACCEPTED_MIME_TYPES = [
  'text/csv',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
]

type SheetRow = Record<
  string,
  string | number | boolean | Date | null | undefined
>

interface LeadTimeUploadProps {
  onParsed: (rows: LeadTimeRow[]) => void
  onUploadSuccess?: () => void
}

const normalizeKey = (value: string) => {
  if (!value) return ''
  return value.replace(/[\s_-]/g, '').toLowerCase()
}

const LeadTimeUpload: React.FC<LeadTimeUploadProps> = ({
  onParsed,
  onUploadSuccess,
}) => {
  const { showFailure, showSuccess, showPageLoader, hidePageLoader } =
    React.useContext<IPixelFactory>(PixelFactoryContext)
  const [fileName, setFileName] = React.useState('')
  const [error, setError] = React.useState('')
  const [isDragging, setIsDragging] = React.useState(false)
  const [localRows, setLocalRows] = React.useState<LeadTimeRow[]>([])
  const [isSubmitting, setIsSubmitting] = React.useState(false)
  const [productType, setProductType] = React.useState('FG')
  const fileInputRef = React.useRef<HTMLInputElement | null>(null)

  const handleDownloadTemplate = () => {
    const templateRows = [
      {
        product_sku: 'SKU-001',
        product_lead_time: 7,
        product_moq: 100,
      },
      {
        product_sku: '',
        product_lead_time: '',
        product_moq: '',
      },
    ]

    const worksheet = XLSX.utils.json_to_sheet(templateRows)
    const workbook = XLSX.utils.book_new()
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Lead Time')
    XLSX.writeFile(workbook, 'lead_time_upload_template.xlsx')
  }

  const extractValue = (row: SheetRow, matcherKeys: string[]): string => {
    // Normalize matcher keys for comparison
    const normalizedMatchers = matcherKeys.map((key) => normalizeKey(key))

    const matchedKey = Object.keys(row).find((key) =>
      normalizedMatchers.includes(normalizeKey(key)),
    )

    if (!matchedKey) {
      return ''
    }

    const value = row[matchedKey]
    if (value === undefined || value === null) {
      return ''
    }

    // Handle both string and numeric values
    if (typeof value === 'number') {
      return value.toString()
    }

    return value.toString().trim()
  }

  const extractNumber = (
    row: SheetRow,
    matcherKeys: string[],
  ): number | undefined => {
    // Normalize matcher keys for comparison
    const normalizedMatchers = matcherKeys.map((key) => normalizeKey(key))

    const matchedKey = Object.keys(row).find((key) =>
      normalizedMatchers.includes(normalizeKey(key)),
    )

    if (!matchedKey) {
      return undefined
    }

    const value = row[matchedKey]

    if (value === undefined || value === null || value === '') {
      return undefined
    }

    // If it's already a number, return it directly
    if (typeof value === 'number' && Number.isFinite(value)) {
      return value
    }

    // Try to parse as number
    const parsedValue = Number(value)
    if (Number.isFinite(parsedValue)) {
      return parsedValue
    }

    return undefined
  }

  const mapRowsToLeadTime = (rows: SheetRow[]): LeadTimeRow[] => {
    return rows
      .map((row) => {
        const productSku = extractValue(row, [
          'product_sku',
          'productsku',
          'sku',
        ])

        // Use extractNumber for numeric values to handle them directly
        const leadTime =
          extractNumber(row, [
            'product_lead_time',
            'lead_time',
            'leadtime',
            'leadtimedays',
            'lead_time_days',
          ]) ?? 0

        const moq =
          extractNumber(row, ['product_moq', 'moq', 'minimumorderquantity']) ??
          0

        const leadTimeRow: LeadTimeRow = {
          // Minimal product object; backend uses SKU for matching
          product: {
            product_id: 0,
            product_name: '',
            product_sku: productSku,
          },
          product_buffer_stock: 0,
          product_sku: productSku,
          // Name is not part of the upload payload; it will be populated from GET response
          product_name: '',
          lead_time: leadTime,
          moq: moq,
          new_lead_time: undefined,
          new_moq: undefined,
        }

        return leadTimeRow
      })
      .filter(
        (item) => item.product_sku && item.lead_time >= 0 && item.moq >= 0,
      )
  }

  const processFile = (file: File) => {
    const isAccepted =
      ACCEPTED_MIME_TYPES.includes(file.type) ||
      file.name.endsWith('.csv') ||
      file.name.endsWith('.xls') ||
      file.name.endsWith('.xlsx')

    if (!isAccepted) {
      const message = 'Please upload a CSV or Excel file (.csv, .xls, .xlsx).'
      setError(message)
      showFailure(message)
      return
    }

    const reader = new FileReader()
    reader.onload = (event: ProgressEvent<FileReader>) => {
      try {
        const buffer = event.target?.result
        if (!buffer) {
          throw new Error('Unable to read file')
        }

        const data = new Uint8Array(buffer as ArrayBuffer)
        const workbook = XLSX.read(data, { type: 'array' })
        const firstSheetName = workbook.SheetNames[0]
        const worksheet = workbook.Sheets[firstSheetName]
        const jsonData = XLSX.utils.sheet_to_json<SheetRow>(worksheet, {
          defval: '',
        })

        if (jsonData.length === 0) {
          throw new Error('Uploaded sheet is empty.')
        }

        const rows = mapRowsToLeadTime(jsonData)
        if (rows.length === 0) {
          throw new Error(
            'No valid rows found. Required columns: product_sku, product_lead_time, product_moq.',
          )
        }

        setFileName(file.name)
        setError('')
        setLocalRows(rows)
        onParsed(rows)
        showSuccess(`Parsed ${rows.length} lead time rows successfully`)
      } catch (err) {
        const message =
          err instanceof Error ? err.message : 'Unable to process the file'
        setError(message)
        setLocalRows([])
        onParsed([])
        showFailure(message)
      }
    }

    reader.readAsArrayBuffer(file)
  }

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const uploadedFile = event.target.files?.[0]
    if (!uploadedFile) {
      return
    }

    processFile(uploadedFile)
    event.target.value = ''
  }

  const handleDrop = (event: React.DragEvent<HTMLLabelElement>) => {
    event.preventDefault()
    setIsDragging(false)
    if (event.dataTransfer.files && event.dataTransfer.files[0]) {
      processFile(event.dataTransfer.files[0])
    }
  }

  const handleDragOver = (event: React.DragEvent<HTMLLabelElement>) => {
    event.preventDefault()
    setIsDragging(true)
  }

  const handleDragLeave = () => {
    setIsDragging(false)
  }

  const handleProceed = () => {
    if (!localRows.length) {
      showFailure('Please upload and parse a sheet before proceeding.')
      return
    }

    setIsSubmitting(true)
    showPageLoader?.()

    const handleSuccess = (res: AjaxResponse<BulkUploadResponse>) => {
      // Check if the response contains an error
      if (res.response?.error) {
        showFailure(res.response.error)
        hidePageLoader?.()
        setIsSubmitting(false)
        return
      }

      const message =
        res.response?.message || 'Lead time data submitted successfully.'
      showSuccess(message)
      hidePageLoader?.()
      setIsSubmitting(false)
      setLocalRows([])
      setFileName('')
      onParsed([])
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
      onUploadSuccess?.()
    }

    const handleError = (err: AjaxError) => {
      console.error('lead-time-upload:submit', err)
      showFailure('Unable to submit lead time data.')
      hidePageLoader?.()
      setIsSubmitting(false)
    }

    submitLeadTimes(localRows, productType).subscribe({
      next: handleSuccess,
      error: handleError,
    })
  }

  return (
    <DcFlexBox
      flexDirection="column"
      padding="20px"
      gap="16px"
      height="100%"
      style={{ overflowY: 'auto' }}
    >
      <DcDiv>
        <DcText style={{ fontSize: '14px', color: '#5c6272' }}>
          Download the &nbsp;
          <a
            style={{ color: colors.primary, cursor: 'pointer' }}
            onClick={handleDownloadTemplate}
            target="_blank"
          >
            template
          </a>
          , add your lead time rows with the required columns, and upload the
          sheet back. Required columns: <b>product_sku</b>,{' '}
          <b>product_lead_time</b>, <b>product_moq</b>.
        </DcText>
      </DcDiv>
      <DcDiv>
        <DcDropDown
          customLabel="Porduct Type"
          value={productType}
          dropdownSize="sm"
          required
          onChange={(e) => setProductType(e.target.value)}
          options={[
            { label: 'Finished Goods (FG)', value: 'FG' },
            { label: 'Manifacturing Materials (MM)', value: 'MM' },
            { label: 'Consumer packaging (CP)', value: 'CP' },
          ]}
        />
      </DcDiv>

      <DropZone
        $isDragging={isDragging}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        htmlFor="lead-time-upload-input"
      >
        <PixelIcon color="#5c4dff" icon={faFileArrowUp} />
        <DcText style={{ fontWeight: 600 }}>Drag & drop your sheet</DcText>
        <DcText style={{ fontSize: '13px', color: '#7a7f92' }}>
          or click to select a file
        </DcText>
        <DcText style={{ fontSize: '12px', color: '#9aa0b5' }}>
          Accepted formats: .csv, .xls, .xlsx
        </DcText>
        {fileName && (
          <DcText style={{ fontSize: '13px', color: '#4a5568' }}>
            Selected file: <b>{fileName}</b>
          </DcText>
        )}
        {error && (
          <DcText style={{ fontSize: '12px', color: '#dc2626' }}>
            {error}
          </DcText>
        )}
        <input
          id="lead-time-upload-input"
          type="file"
          accept=".csv,.xls,.xlsx"
          ref={fileInputRef}
          style={{ display: 'none' }}
          onChange={handleFileChange}
        />
      </DropZone>

      {localRows.length > 0 && (
        <DcDiv>
          <DcText style={{ fontSize: '13px', color: '#6b7280' }}>
            Parsed {localRows.length} row{localRows.length !== 1 ? 's' : ''}{' '}
            successfully.
          </DcText>
        </DcDiv>
      )}

      <DcFlexBox justifyContent="space-between" height="auto">
        <DcButton
          variant="link"
          size="sm"
          disabled={!fileName && localRows.length === 0}
          onClick={() => {
            setFileName('')
            setError('')
            setLocalRows([])
            onParsed([])
            if (fileInputRef.current) {
              fileInputRef.current.value = ''
            }
          }}
        >
          Clear
        </DcButton>
        <DcButton
          variant="primary"
          size="sm"
          disabled={isSubmitting || localRows.length === 0}
          onClick={handleProceed}
        >
          {isSubmitting ? 'Submitting…' : 'Proceed'}
        </DcButton>
      </DcFlexBox>
    </DcFlexBox>
  )
}

export default LeadTimeUpload
